import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import { useDeleteUser } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';

export function AccountDeletionPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const deleteUserMutation = useDeleteUser();
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    if (confirmText !== 'DELETE MY ACCOUNT') {
      toast.error('Please type "DELETE MY ACCOUNT" to confirm deletion');
      return;
    }

    setIsDeleting(true);
    
    try {
      await deleteUserMutation.mutateAsync({
        confirmDeletion: true,
        confirmText: 'DELETE MY ACCOUNT',
      });

      toast.success('Account deleted successfully');
      
      // User will be automatically signed out by Firebase Auth deletion
      // Redirect to home page after a short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Failed to delete account:', error);
      toast.error('Failed to delete account. Please try again.');
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    navigate('/settings');
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-red-900 mb-2">
          ⚠️ Delete Account
        </h1>
        <p className="text-lg text-gray-600">
          This action cannot be undone. All your data will be permanently deleted.
        </p>
      </div>

      {/* Warning Box */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              What will be deleted:
            </h3>
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              <li>Your user account and profile</li>
              <li>All sites you own</li>
              <li>All feedback votes and comments</li>
              <li>All analytics and statistics</li>
              <li>All API keys and webhooks</li>
              <li>All payment and subscription history</li>
              <li>All exports and logs</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Current User Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-2">
          Account to be deleted:
        </h3>
        <div className="flex items-center space-x-3">
          <img
            className="h-8 w-8 rounded-full"
            src={user?.photoURL || `https://ui-avatars.com/api/?name=${user?.email}&background=6366f1&color=fff`}
            alt={user?.displayName || 'User'}
          />
          <div>
            <p className="text-sm font-medium text-gray-900">
              {user?.displayName || 'No name'}
            </p>
            <p className="text-sm text-gray-600">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Confirmation Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Confirm Account Deletion
        </h3>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Type <span className="font-mono bg-gray-100 px-1 rounded">DELETE MY ACCOUNT</span> to confirm:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            placeholder="DELETE MY ACCOUNT"
            disabled={isDeleting}
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={handleCancel}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteAccount}
            disabled={isDeleting || confirmText !== 'DELETE MY ACCOUNT'}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
          >
            {isDeleting ? 'Deleting Account...' : 'Delete Account'}
          </button>
        </div>
      </div>

      {/* Legal Notice */}
      <div className="text-center text-xs text-gray-500 max-w-md mx-auto">
        <p>
          This deletion complies with GDPR and other privacy regulations. 
          If you need a copy of your data before deletion, please <button onClick={() => window.alert('Please email us at hello@microfb.com')} className="text-indigo-600 hover:text-indigo-500 underline">contact support</button>.
        </p>
      </div>
    </div>
  );
} 